import type { OutputPreviewTab } from '@/types/player/simulation-cue-sheet.types';
import {
  ensureSimulationBroadcastRoomCamIds,
  SIMULATION_ROOM_CAM_PLACEHOLDER_ID,
} from '@/helpers/simulation-cue-engine.helper';
import { resolveSimulationPlayerType } from '@/helpers/resolve-output-preview-layers';

export type NextCuePreviewShowMode = 'mediaOnly' | 'both' | 'speakerOnly';

export type NextCuePreviewFocusMode = 'room' | 'remote' | 'both';

export type NextCuePreviewRequest = {
  planFlowId: string;
  selectedItemIds: string[];
  showMode: NextCuePreviewShowMode;
  playerType: 'stagePlayer' | 'roomPlayer' | 'onlinePlayer';
  contextSlotId?: string;
  contextScope?: string;
  /** Part of the cache key. Remote focus with speakers omits the room-cam placeholder. */
  focusMode?: NextCuePreviewFocusMode;
};

/**
 * Simulation-preview-layers request for one look-ahead cue row, encoded as a string.
 *
 * The string is both the request and its cache key, so effects can depend on it by value: an equal
 * look-ahead target must never look like a new one, otherwise a re-render would cancel the fetch
 * that is already loading it.
 *
 * @returns `''` when there is nothing to request.
 */
export function buildNextCuePreviewRequestKey({
  planFlowId,
  tab,
  itemId,
  slotId,
  scope,
  showMode,
  remoteSpeakerIds,
  focusMode,
}: {
  planFlowId?: string;
  tab: OutputPreviewTab;
  itemId?: string;
  slotId?: string | null;
  scope?: string | null;
  showMode: NextCuePreviewShowMode;
  /** On-stage speakers who stay for this next row. Broadcast uses them; stage and room stay on the media. */
  remoteSpeakerIds?: string[];
  /**
   * Camera focus. Remote focus with speakers does not ask for a room camera.
   * Room focus with speakers means the room camera is absent, so the speakers are the fallback.
   */
  focusMode?: NextCuePreviewFocusMode;
}): string {
  if (!planFlowId || !itemId) {
    return '';
  }
  const playerType = resolveSimulationPlayerType(tab);
  const speakers = (remoteSpeakerIds ?? []).filter((id) => id.startsWith('remoteSpeaker:'));
  const baseIds = playerType === 'onlinePlayer' ? [itemId, ...speakers] : [itemId];
  const speakersStandInForRoomCam = speakers.length > 0 && (focusMode === 'remote' || focusMode === 'room');
  const selectedItemIds =
    playerType === 'onlinePlayer'
      ? speakersStandInForRoomCam
        ? baseIds
        : ensureSimulationBroadcastRoomCamIds(baseIds, showMode)
      : baseIds.filter((id) => id !== SIMULATION_ROOM_CAM_PLACEHOLDER_ID);
  const contextSlotId = typeof slotId === 'string' && slotId.length > 0 ? slotId : undefined;
  const contextScope = typeof scope === 'string' && scope.length > 0 ? scope : undefined;

  const request: NextCuePreviewRequest = {
    planFlowId,
    selectedItemIds,
    showMode,
    playerType,
    ...(contextSlotId ? { contextSlotId } : {}),
    ...(contextScope ? { contextScope } : {}),
    ...(focusMode ? { focusMode } : {}),
  };
  return JSON.stringify(request);
}

/**
 * Sequential takes must not remint Next via HTTP (promote `_lookahead2` instead).
 * Jump/segment latches `nextRecalculating` and look-ahead compose is deferred — HTTP must run.
 */
export function shouldSkipLiveNextPreviewHttpFallback(params: {
  performMode: 'play' | 'simulation';
  sequentialPromotePending: boolean;
  transportBusy: boolean;
  nextRecalculating: boolean;
}): boolean {
  if (params.performMode !== 'play') {
    return false;
  }
  if (params.nextRecalculating) {
    return false;
  }
  return params.sequentialPromotePending || params.transportBusy;
}

export type LiveNextPreviewHttpDecision = 'applied' | 'keep-busy' | 'remint';

/**
 * LIVE Next HTTP fallback.
 * Sequential promote: keep-busy (WS promotes `_lookahead2`) even when painted layers are not yet Next.
 * Transport `skipLiveHttpFallback`: keep-busy only while mounted layers already match Next; otherwise remint.
 * Blanking is a separate overlay — keep-busy must not leave a leftover item painted.
 */
export function resolveLiveNextPreviewHttpDecision(params: {
  sequentialPromotePending: boolean;
  skipLiveHttpFallback: boolean;
  paintedLayersMatchNext: boolean;
  appliedRequestKeyMatches: boolean;
  /** Same Next item, operator changed its show mode. Rebuild even if a promote would keep the old picture. */
  showModeChanged?: boolean;
}): LiveNextPreviewHttpDecision {
  if (params.showModeChanged) {
    return 'remint';
  }
  if (params.appliedRequestKeyMatches && params.paintedLayersMatchNext) {
    return 'applied';
  }
  if (params.sequentialPromotePending) {
    return 'keep-busy';
  }
  if (params.skipLiveHttpFallback && params.paintedLayersMatchNext) {
    return 'keep-busy';
  }
  return 'remint';
}

/** Click → black Next spinner until the take latch clears and the new Next can paint. */
export function shouldBlankLiveNextPreview(params: {
  transportBusy: boolean;
  sequentialPromotePending: boolean;
  nextRecalculating: boolean;
  isPending: boolean;
  nextHttpInFlight: boolean;
}): boolean {
  return (
    params.nextRecalculating ||
    params.isPending ||
    params.transportBusy ||
    params.sequentialPromotePending ||
    params.nextHttpInFlight
  );
}

/**
 * LIVE HTTP Next fallback requests the show mode the operator is looking at.
 * A presentation in speaker-only is the speaker, not the deck still.
 */
export function resolveLiveNextPreviewHttpShowMode(params: {
  showMode: NextCuePreviewShowMode;
  isPresentation: boolean;
}): NextCuePreviewShowMode {
  return params.showMode;
}

function normalizeNextCuePreviewShowMode(showMode?: string): NextCuePreviewShowMode | undefined {
  if (showMode === 'both' || showMode === 'mixedView') {
    return 'both';
  }
  if (showMode === 'speakerOnly' || showMode === 'mediaOnly') {
    return showMode;
  }
  return undefined;
}

/**
 * Pushed `_lookahead:*` identity ignores showMode. After an operator Next overlay click,
 * keep the push only when its stamped showMode already matches the hold — otherwise HTTP remints.
 */
export function lookAheadPushMatchesHeldShowMode(params: {
  metaShowMode?: string;
  heldShowMode: NextCuePreviewShowMode | null;
}): boolean {
  if (params.heldShowMode == null) {
    return true;
  }
  return normalizeNextCuePreviewShowMode(params.metaShowMode) === params.heldShowMode;
}

function previewLayerShowsCamera(layer: { itemId?: string; componentId?: string; visible?: boolean }): boolean {
  if (layer.visible === false) {
    return false;
  }
  const itemId = layer.itemId ?? '';
  return (
    itemId.startsWith('roomCam:') ||
    itemId.startsWith('remoteSpeaker:') ||
    layer.componentId === 'livekitTrackRoomCam' ||
    layer.componentId === 'livekitTrackRoomCamGrid' ||
    layer.componentId === 'livekitTrackRemoteSpeaker'
  );
}

function previewLayerShowsCueMedia(layer: { itemId?: string; componentId?: string; visible?: boolean }): boolean {
  if (layer.visible === false || previewLayerShowsCamera(layer)) {
    return false;
  }
  const itemId = layer.itemId ?? '';
  if (itemId.startsWith('media:') || itemId.startsWith('composite:') || itemId.startsWith('virtualMedia:')) {
    return true;
  }
  // Speaker lines sit on every show mode. They are not the cue item. Other ids still count
  // when the layer is html, image, or video.
  return (
    !itemId.startsWith('overlay:') &&
    (layer.componentId === 'html' || layer.componentId === 'image' || layer.componentId === 'video')
  );
}

/**
 * The painted Next may stay only when it is already the requested composition.
 * Media-only rejects a camera. Speaker and mixed reject a media-only snapshot.
 */
export function previewLayersFitShowMode(
  layers: Array<{ itemId?: string; componentId?: string; visible?: boolean }>,
  showMode: NextCuePreviewShowMode,
): boolean {
  const camera = layers.some(previewLayerShowsCamera);
  const media = layers.some(previewLayerShowsCueMedia);
  if (showMode === 'speakerOnly') {
    return camera && !media;
  }
  if (showMode === 'both') {
    return camera && media;
  }
  return !camera;
}

/**
 * This tab's live picture is still the previous show mode.
 * A missing camera is not the previous mode: Broadcast keeps the live picture and paints a placeholder.
 */
export function previewPictureStillShowsPreviousShowMode(
  layers: Array<{ itemId?: string; componentId?: string; visible?: boolean }>,
  showMode: NextCuePreviewShowMode,
): boolean {
  if (layers.length === 0) {
    return false;
  }
  const media = layers.some(previewLayerShowsCueMedia);
  const camera = layers.some(previewLayerShowsCamera);
  if (showMode === 'speakerOnly') {
    return media;
  }
  if (showMode === 'both') {
    return !media;
  }
  return camera;
}

/** A real room camera in the live picture. Placeholders do not count; Room focus falls back to speakers without one. */
export function livePictureHasRoomCamera(
  byPlayer:
    | Record<string, { layers?: Array<{ itemId?: string; componentId?: string; visible?: boolean }> }>
    | undefined,
): boolean {
  if (!byPlayer) return false;
  for (const entry of Object.values(byPlayer)) {
    for (const layer of entry?.layers ?? []) {
      if (layer.visible === false) continue;
      const itemId = layer.itemId ?? '';
      if (itemId.includes('placeholder')) continue;
      if (
        itemId.startsWith('roomCam:') ||
        layer.componentId === 'livekitTrackRoomCam' ||
        layer.componentId === 'livekitTrackRoomCamGrid'
      ) {
        return true;
      }
    }
  }
  return false;
}

/** Counterpart of {@link buildNextCuePreviewRequestKey}. */
export function parseNextCuePreviewRequestKey(key: string): NextCuePreviewRequest | null {
  if (!key) {
    return null;
  }
  try {
    return JSON.parse(key) as NextCuePreviewRequest;
  } catch {
    return null;
  }
}
